import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../api';
import GlassCard from './ui/GlassCard';
import Badge from './ui/Badge';
import Btn from './ui/Btn';
import Field from './ui/Field';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './ui/EmptyState';
import SearchBar from './ui/SearchBar';
import Header from './ui/Header';
import InfoTable from './ui/InfoTable';
import Icon from './ui/Icon';
import PillBtn from './ui/PillBtn';
import LinkButton from './ui/LinkButton';
import { formatDate } from '../utils/format';

interface Kit {
  id: number;
  title: string;
  content?: string | null;
  source?: string | null;
  location?: string | null;
  repair_id?: number | null;
  repair_device?: string | null;
  repair_client_name?: string | null;
  repair_archived?: number;
  repair_archive_id?: number | null;
  given_at?: string | null;
  created_at?: string;
}

interface ArchivedKit extends Kit {
  original_kit_id?: number | null;
  deleted_at: string;
  delete_reason?: string | null;
}

interface KitForm {
  title: string;
  content: string;
  source: string;
  location: string;
  repair_id: string;
}

interface Repair {
  id: number;
  device: string;
  client_name?: string;
}

interface KitsSectionProps {
  onOpenRepair?: (repairId: number, archived: boolean) => void;
  openKitId?: number | null;
  openKitArchived?: boolean;
  onKitOpened?: () => void;
}

type View = 'list' | 'detail' | 'form';
type Tab = 'active' | 'archive';

const EMPTY_FORM: KitForm = { title: '', content: '', source: '', location: '', repair_id: '' };

const repairLabel = (r: Repair) =>
  `#${r.id} · ${r.device}${r.client_name ? ' · ' + r.client_name : ''}`;

// Ремонт мог уехать в архив — подпись приходит оттуда с пометкой repair_archived.
// Если его нет ни в работе, ни в архиве, показывать нечего.
const kitRepairLabel = (kit: Kit) => {
  if (!kit.repair_id || !kit.repair_device) return null;
  const label = `#${kit.repair_id} · ${kit.repair_device}${kit.repair_client_name ? ' · ' + kit.repair_client_name : ''}`;
  return kit.repair_archived ? `${label} · в архиве` : label;
};

const matchesSearch = (kit: Kit, query: string) => {
  const q = query.toLowerCase();
  return (
    (kit.title || '').toLowerCase().includes(q) ||
    (kit.content || '').toLowerCase().includes(q) ||
    (kit.source || '').toLowerCase().includes(q) ||
    (kit.location || '').toLowerCase().includes(q) ||
    (kit.repair_device || '').toLowerCase().includes(q) ||
    (kit.repair_client_name || '').toLowerCase().includes(q)
  );
};

// Пропавший роут отдаёт SPA-заглушку вместо JSON — пустой список лучше белого экрана.
const asList = (value: unknown): any[] => (Array.isArray(value) ? value : []);

const KitsSection: React.FC<KitsSectionProps> = ({ onOpenRepair, openKitId, openKitArchived, onKitOpened }) => {
  const [kits, setKits] = useState<Kit[]>([]);
  const [repairs, setRepairs] = useState<Repair[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('active');
  const [view, setView] = useState<View>('list');
  const [selected, setSelected] = useState<Kit | null>(null);
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [form, setForm] = useState<KitForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<KitForm>>({});
  const [saving, setSaving] = useState(false);
  const [giving, setGiving] = useState(false);
  const [confirmGive, setConfirmGive] = useState(false);

  const [archive, setArchive] = useState<ArchivedKit[]>([]);
  const [archiveLoaded, setArchiveLoaded] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [selectedArchived, setSelectedArchived] = useState<ArchivedKit | null>(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);

  const load = useCallback(async () => {
    try {
      const [kitsRes, repairsRes] = await Promise.all([api.getKits(), api.getRepairs()]);
      setKits(asList(kitsRes.data));
      setRepairs(asList(repairsRes.data));
    } catch (err) {
      console.error('Error loading kits', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadArchive = useCallback(async () => {
    setArchiveLoading(true);
    try {
      const res = await api.getKitsArchive();
      setArchive(asList(res.data));
      setArchiveLoaded(true);
    } catch (err) {
      console.error('Error loading kits archive', err);
    } finally {
      setArchiveLoading(false);
    }
  }, []);

  // Переход из карточки ремонта: открываем нужную запись, как только её список загружен.
  // Архивную — после переключения вкладки, которое само подтянет архив.
  useEffect(() => {
    if (!openKitId) return;
    if (openKitArchived) {
      if (tab !== 'archive') { setTab('archive'); setView('list'); setSelected(null); return; }
      if (!archiveLoaded) return;
      const kit = archive.find(k => k.id === openKitId);
      if (kit) setSelectedArchived(kit);
      onKitOpened?.();
      return;
    }
    if (loading) return;
    const kit = kits.find(k => k.id === openKitId);
    if (kit) {
      setTab('active');
      setSelected(kit);
      setView('detail');
    }
    onKitOpened?.();
  }, [openKitId, openKitArchived, loading, kits, tab, archiveLoaded, archive, onKitOpened]);

  // Архив тянем только когда на него смотрят, и заново после каждого удаления.
  useEffect(() => {
    if (tab === 'archive' && !archiveLoaded && !archiveLoading) loadArchive();
  }, [tab, archiveLoaded, archiveLoading, loadArchive]);

  const filtered = useMemo(() => kits.filter(k => matchesSearch(k, search)), [kits, search]);
  const filteredArchive = useMemo(() => archive.filter(k => matchesSearch(k, search)), [archive, search]);

  const switchTab = (next: Tab) => {
    setTab(next);
    setView('list');
    setSelected(null);
    setSelectedArchived(null);
    setSearch('');
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setSelected(null);
    setView('form');
  };

  const openEdit = (kit: Kit) => {
    setForm({
      title: kit.title,
      content: kit.content || '',
      source: kit.source || '',
      location: kit.location || '',
      repair_id: kit.repair_id ? String(kit.repair_id) : '',
    });
    setErrors({});
    setSelected(kit);
    setView('form');
  };

  const save = async () => {
    const err: Partial<KitForm> = {};
    if (!form.title.trim()) err.title = 'Поле обязательно';
    if (Object.keys(err).length) { setErrors(err); return; }

    const payload = {
      title: form.title.trim(),
      content: form.content.trim() || null,
      source: form.source.trim() || null,
      location: form.location.trim() || null,
      repair_id: form.repair_id ? Number(form.repair_id) : null,
    };

    setSaving(true);
    try {
      if (selected) {
        const res = await api.updateKit(selected.id, payload);
        const updated: Kit = res.data;
        setKits(list => list.map(k => k.id === updated.id ? updated : k));
        setSelected(updated);
        setView('detail');
      } else {
        const res = await api.createKit(payload);
        setKits(list => [res.data, ...list]);
        setView('list');
      }
    } catch (err) {
      console.error('Save error', err);
    } finally {
      setSaving(false);
    }
  };

  const give = async () => {
    if (!selected) return;
    setGiving(true);
    try {
      await api.giveKit(selected.id);
      setKits(list => list.filter(k => k.id !== selected.id));
      setArchiveLoaded(false);
      setConfirmGive(false);
      setView('list');
    } catch (err) {
      console.error('Give error', err);
    } finally {
      setGiving(false);
    }
  };

  const del = async () => {
    if (!selected) return;
    try {
      await api.deleteKit(selected.id);
      setKits(list => list.filter(k => k.id !== selected.id));
      setArchiveLoaded(false);
      setView('list');
      setConfirm(false);
    } catch (err) {
      console.error('Delete error', err);
    }
  };

  const restore = async () => {
    if (!selectedArchived) return;
    try {
      const res = await api.restoreKit(selectedArchived.id);
      setKits(list => [res.data, ...list]);
      setArchive(list => list.filter(k => k.id !== selectedArchived.id));
      setSelectedArchived(null);
      setConfirmRestore(false);
    } catch (err) {
      console.error('Restore error', err);
    }
  };

  const purge = async () => {
    if (!selectedArchived) return;
    try {
      await api.deleteKitFromArchive(selectedArchived.id);
      setArchive(list => list.filter(k => k.id !== selectedArchived.id));
      setSelectedArchived(null);
      setConfirmPurge(false);
    } catch (err) {
      console.error('Delete error', err);
    }
  };

  // Живой ремонт открывается в «Ремонтах», архивный — в их архиве по id архивной записи.
  const repairCell = (kit: Kit) => {
    const label = kitRepairLabel(kit);
    if (!label || !kit.repair_id || !onOpenRepair) return label || '—';
    if (kit.repair_archived) {
      if (!kit.repair_archive_id) return label;
      return <LinkButton onClick={() => onOpenRepair(kit.repair_archive_id as number, true)}>{label}</LinkButton>;
    }
    return <LinkButton onClick={() => onOpenRepair(kit.repair_id as number, false)}>{label}</LinkButton>;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header title="Комплектация" />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)' }}>
          Загрузка...
        </div>
      </div>
    );
  }

  if (view === 'form') return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title={selected ? 'Редактировать' : 'Новая комплектация'} onBack={() => setView(selected ? 'detail' : 'list')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 100px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 520 }}>
          <Field label="Название" required error={errors.title}>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Коробка от ноутбука Asus" />
          </Field>
          <Field label="Что было">
            <textarea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="Блок питания, шлейф, 4 винта, крышка..."
              rows={4}
            />
          </Field>
          <Field label="У кого взял">
            <input value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} placeholder="Имя или компания" />
          </Field>
          <Field label="Где лежит">
            <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Стеллаж, полка, коробка" />
          </Field>
          <Field label="Ремонт">
            <select value={form.repair_id} onChange={e => setForm(f => ({ ...f, repair_id: e.target.value }))}>
              <option value="">Без привязки</option>
              {repairs.map(r => (
                <option key={r.id} value={r.id}>{repairLabel(r)}</option>
              ))}
            </select>
          </Field>
        </div>
      </div>
      <div style={{ padding: '16px 20px', borderTop: '1px solid var(--glass-border)', display: 'flex', gap: 10 }}>
        <Btn variant="primary" onClick={save} icon="check" disabled={saving} style={{ flex: 1, justifyContent: 'center' }}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Btn>
        <Btn variant="ghost" onClick={() => setView(selected ? 'detail' : 'list')}>Отмена</Btn>
      </div>
    </div>
  );

  if (view === 'detail' && selected) return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {confirm && (
        <ConfirmDialog
          message={`Удалить «${selected.title}»? Запись уйдёт в архив.`}
          onConfirm={del}
          onCancel={() => setConfirm(false)}
        />
      )}
      {confirmGive && (
        <ConfirmDialog
          message={`Отдать «${selected.title}»? Запись уйдёт в архив.`}
          onConfirm={give}
          onCancel={() => setConfirmGive(false)}
          confirmLabel="Отдать"
        />
      )}
      <Header title="Комплектация" onBack={() => setView('list')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text0)', marginBottom: 16 }}>
          {selected.title}
        </h2>
        <InfoTable rows={[
          ['Что было', selected.content
            ? <span style={{ whiteSpace: 'pre-wrap' }}>{selected.content}</span>
            : '—'],
          ['У кого взял', selected.source || '—'],
          ['Где лежит', selected.location || '—'],
          ['Ремонт', repairCell(selected)],
          ['Добавлено', formatDate(selected.created_at)],
        ]} />
      </div>
      <div style={{ padding: '16px 20px', borderTop: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Btn
          variant="primary"
          icon="check"
          onClick={() => setConfirmGive(true)}
          disabled={giving}
          style={{ justifyContent: 'center' }}
        >
          Отдать
        </Btn>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="ghost" icon="edit" onClick={() => openEdit(selected)} style={{ flex: 1, justifyContent: 'center' }}>
            Редактировать
          </Btn>
          <Btn variant="danger" icon="trash" onClick={() => setConfirm(true)}>Удалить</Btn>
        </div>
      </div>
    </div>
  );

  if (selectedArchived) return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {confirmRestore && (
        <ConfirmDialog
          message="Восстановить комплектацию из архива?"
          onConfirm={restore}
          onCancel={() => setConfirmRestore(false)}
          confirmLabel="Восстановить"
        />
      )}
      {confirmPurge && (
        <ConfirmDialog
          message="Удалить окончательно? Это действие нельзя отменить!"
          onConfirm={purge}
          onCancel={() => setConfirmPurge(false)}
          confirmLabel="Удалить навсегда"
        />
      )}
      <Header title="Архив" onBack={() => setSelectedArchived(null)} />
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text0)', marginBottom: 10 }}>
            {selectedArchived.title}
          </h2>
          <Badge variant="paid">{selectedArchived.given_at ? 'Отдано' : 'Архив'}</Badge>
        </div>
        <InfoTable rows={[
          ['Что было', selectedArchived.content
            ? <span style={{ whiteSpace: 'pre-wrap' }}>{selectedArchived.content}</span>
            : '—'],
          ['У кого взял', selectedArchived.source || '—'],
          ['Где лежит', selectedArchived.location || '—'],
          ['Ремонт', repairCell(selectedArchived)],
          ['Добавлено', formatDate(selectedArchived.created_at)],
          [selectedArchived.given_at ? 'Отдано' : 'Архивировано', formatDate(selectedArchived.deleted_at)],
          selectedArchived.delete_reason ? ['Причина', selectedArchived.delete_reason] : null,
        ]} />
        <div style={{ marginTop: 16, background: 'var(--glass-bg)', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 12, color: 'var(--text2)', textAlign: 'center', border: '1px solid var(--glass-border)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
          {selectedArchived.given_at ? 'Отдано · только просмотр' : 'Архивная запись · только просмотр'}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Btn variant="subtle" icon="back" onClick={() => setConfirmRestore(true)} style={{ flex: 1, justifyContent: 'center' }}>
            Восстановить
          </Btn>
          <Btn variant="danger" icon="trash" onClick={() => setConfirmPurge(true)} style={{ flex: 1, justifyContent: 'center' }}>
            Удалить навсегда
          </Btn>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header
        title="Комплектация"
        action={tab === 'active' ? <Btn variant="primary" icon="plus" onClick={openAdd}>Добавить</Btn> : undefined}
      />
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Название, содержимое, место..." />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <PillBtn active={tab === 'active'} onClick={() => switchTab('active')}>Активные</PillBtn>
          <PillBtn active={tab === 'archive'} onClick={() => switchTab('archive')}>Архив</PillBtn>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 100px' }}>
        {tab === 'active' ? (
          filtered.length === 0 ? (
            <EmptyState
              icon="box"
              title={search ? 'Ничего не найдено' : 'Пока пусто'}
              sub={search ? 'Попробуйте другой запрос' : 'Добавьте первую запись'}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filtered.map(kit => {
                const repair = kitRepairLabel(kit);
                return (
                  <GlassCard
                    key={kit.id}
                    onClick={() => { setSelected(kit); setView('detail'); }}
                    style={{ padding: '14px 16px', cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text0)', marginBottom: 4 }}>
                          {kit.title}
                        </div>
                        {kit.content && (
                          <div style={{
                            fontSize: 13,
                            color: 'var(--text2)',
                            marginBottom: 4,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {kit.content}
                          </div>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: 'var(--text3)' }}>
                          {kit.location && <span>Лежит: {kit.location}</span>}
                          {kit.source && <span>От: {kit.source}</span>}
                          {repair && <span>Ремонт {repair}</span>}
                        </div>
                      </div>
                      <Icon name="chevron" size={18} color="var(--text3)" />
                    </div>
                  </GlassCard>
                );
              })}
            </div>
          )
        ) : archiveLoading && !archiveLoaded ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>Загрузка...</div>
        ) : filteredArchive.length === 0 ? (
          <EmptyState
            icon="archive"
            title={search ? 'Ничего не найдено' : 'Архив пуст'}
            sub={search ? 'Попробуйте другой запрос' : 'Удалённые записи появятся здесь'}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredArchive.map(kit => {
              const repair = kitRepairLabel(kit);
              return (
                <GlassCard
                  key={kit.id}
                  onClick={() => setSelectedArchived(kit)}
                  style={{ padding: '14px 16px', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text0)' }}>
                          {kit.title}
                        </div>
                        {kit.given_at && <Badge variant="paid">Отдано</Badge>}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: 'var(--text3)' }}>
                        {kit.location && <span>Лежало: {kit.location}</span>}
                        {kit.source && <span>От: {kit.source}</span>}
                        {repair && <span>Ремонт {repair}</span>}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>
                      {formatDate(kit.deleted_at)}
                    </div>
                  </div>
                </GlassCard>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default KitsSection;
